import React from 'react';
// Mui
import { styled } from '@mui/material';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
// Shared
import { CustomBadge } from 'src/app/shared/Badge';
// Interface
import { Notification as NotificationObject } from 'src/app/models/AppNotification';
// Icons
import ClearIcon from '@mui/icons-material/Clear';
import { NotificationType } from 'src/app/models/notificationTypes';

interface INotificationItem extends MenuItemProps {
    bookingtype?: 'abc' | 'ccd';
}

interface IProps {
    notification: NotificationObject;
    notificationResolve: (id: string) => void;
    notificationClose: (id: string) => void;
    gotoBooking: (bookingRef: string, bookingJourneyRef: string) => void;
    key: any;
}

const getWindowType = {
    Collection: {
        title: 'Collection Window',
        message:
            'The driver for booking $bookingRef is at risk of missing the colection window. Please contact the customer.',
    },
    Injection: {
        title: 'Injection Window',
        message:
            '$bookingRef is at risk of missing the injection window. Please look to inject these bags immediately.',
    },
    Delivery: {
        title: 'Delivery Window',
        message: '$bookingRef is at risk of missing the delivery window. Please contact the customer.',
    },
    MissingBag: {
        title: 'Missing Bag',
        message: '$bookingRef has $missingBagCount bag missing. Please contact relevant partners for more information.',
        message_2:
            '$bookingRef have $missingBagCount bags missing. Please contact relevant partners for more information.',
    },
};

const Notification = styled(MenuItem, {
    shouldForwardProp(prop) {
        return prop !== 'bookingtype';
    },
})<INotificationItem>(({ theme, bookingtype }) => ({
    display: 'block',
    padding: '16px',
    marginBottom: '10px',
    borderRadius: '6px',
    cursor: 'default',
    backgroundColor: bookingtype === 'abc' ? theme.palette.alpha.purple['5'] : theme.palette.alpha.error['3'],
    ':hover': {
        backgroundColor: bookingtype === 'abc' ? theme.palette.alpha.purple['15'] : theme.palette.alpha.error['6'],
    },
}));

const NotificationItem: React.FC<IProps> = ({ notification, notificationResolve, notificationClose, gotoBooking }) => {
    const context = getWindowType[NotificationType[notification.NotificationType]];
    const getNormalizedText = (text: string, params) => {
        let message = text;
        Object.keys(params).forEach((key) => {
            message = message.replace(`$${key}`, params[key]);
        });
        return message;
    };

    let message = context.message.split('$bookingRef');
    if (NotificationType[notification.NotificationType] === 'MissingBag') {
        const text = Number(notification.NotificationText.missingBagCount) === 1 ? context.message : context.message_2;
        message = getNormalizedText(text, notification.NotificationText).split('$bookingRef');
    }

    return (
        <Notification bookingtype={notification.ProductType === 1 ? 'abc' : 'ccd'} disableRipple disableTouchRipple>
            <Grid
                container
                spacing={'16px'}
                whiteSpace='pre-wrap'
                sx={{ justifyContent: ['space-between', 'space-between', 'start'] }}>
                <Grid item>
                    <CustomBadge
                        sx={{ fontSize: '10px', lineHeight: '12px', ml: 0 }}
                        purple={notification.ProductType === 1}
                        error={notification.ProductType === 2}>
                        {notification.ProductType === 1 ? 'ABC' : 'CCD'}
                    </CustomBadge>
                </Grid>
                <Grid item sx={{ order: [2, 2, 0], maxWidth: ['100%', '100%', '413px'] }}>
                    <Grid container alignItems='center' justifyContent='space-between'>
                        <Grid item>
                            <Typography sx={{ lineHeight: '24px' }}>
                                {context.title}
                                {notification.NotificationWindow && ` - ${notification.NotificationWindow}`}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', lineHeight: '16px' }}>
                                {message[0] && message[0] + ' '}
                                <Typography
                                    component='span'
                                    onClick={() =>
                                        gotoBooking(notification.BookingReference, notification.BookingJourneyReference)
                                    }
                                    sx={{
                                        color: (t) => t.palette.purple.main,
                                        fontSize: '0.75rem',
                                        lineHeight: '16px',
                                        cursor: 'pointer',
                                    }}>
                                    {notification.BookingJourneyReference}
                                </Typography>{' '}
                                {message[1]}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    <IconButton sx={{ p: 0 }} onClick={() => notificationClose(notification.NotificationId)}>
                        <ClearIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid container justifyContent='space-between' mt='12px' alignItems='center'>
                <Grid item sx={{ pl: [0, 0, '54.8px'] }}>
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            lineHeight: '12px',
                            color: (theme) => theme.palette.alpha.primary['40'],
                        }}>
                        {notification.CreatedDateText} | {notification.CreatedTimeText}
                    </Typography>
                </Grid>
                <Grid item>
                    <Button
                        variant='contained'
                        color='purple'
                        onClick={() => notificationResolve(notification.NotificationId)}
                        sx={{ fontSize: '0.75rem', lineHeight: '12px', px: '10px', py: '6px' }}>
                        Resolve
                    </Button>
                </Grid>
            </Grid>
        </Notification>
    );
};

export default NotificationItem;
